import { useEffect, FormEventHandler } from 'react';
import { GuestLayout } from '@/Layouts/GuestLayout';
import { InputError } from '@/Components/InputError';
import { InputLabel } from '@/Components/InputLabel';
import { PrimaryButton } from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import { Link, useForm } from '@inertiajs/react';
import { Notice } from '@/types';
import { Head } from '@/Components/Head';

export default function Login({
    status,
    canResetPassword,
    selfServeTrialistsEnabled,
    notice,
}: {
    status?: string;
    canResetPassword: boolean;
    selfServeTrialistsEnabled: boolean;
    notice: Notice;
}) {
    const { data, setData, post, processing, errors, reset } = useForm({
        email: '',
        password: '',
        remember: false,
    });

    useEffect(() => {
        return () => {
            reset('password');
        };
    }, []);

    const submit: FormEventHandler = (event) => {
        event.preventDefault();

        post(route('login'));
    };

    return (
        <GuestLayout showEmergencyBanner notice={notice}>
            <Head
                title={
                    'ReelFlow Login &ndash; Engage your website visitors through interactive video'
                }
            />

            <img src="/img/logo-motif.png" className="w-[100px] my-4" />

            <h1 className="text-heading-one text-brand-blue font-semibold font-area-normal">
                Sign in to ReelFlow
            </h1>

            {status && (
                <div className="mb-4 font-medium text-sm text-green-600">
                    {status}
                </div>
            )}

            <form onSubmit={submit} className={'grid gap-4'}>
                <div>
                    <InputLabel htmlFor="email" value="Work Email" />

                    <TextInput
                        autoFocus
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="mt-1 block w-full"
                        autoComplete="username"
                        onChange={(event) =>
                            setData('email', event.target.value)
                        }
                    />

                    <InputError message={errors.email} className="mt-2" />
                </div>

                <div>
                    <InputLabel htmlFor="password" value="Password" />

                    <TextInput
                        id="password"
                        type="password"
                        name="password"
                        value={data.password}
                        className="mt-1 block w-full"
                        autoComplete="current-password"
                        onChange={(e) => setData('password', e.target.value)}
                    />

                    <InputError message={errors.password} className="mt-2" />
                </div>

                <div>
                    <PrimaryButton disabled={processing}>Sign in</PrimaryButton>
                </div>

                <div>
                    {canResetPassword && (
                        <Link
                            href={route('password.request')}
                            className="underline text-brand-blue rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue hover:text-brand-ocean">
                            Forgot password?
                        </Link>
                    )}
                    {selfServeTrialistsEnabled && (
                        <>
                            <br />
                            <Link
                                href={route('signup')}
                                className="underline text-brand-blue rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue hover:text-brand-ocean">
                                No account? Sign up
                            </Link>
                        </>
                    )}
                </div>

                <div className="text-sm text-brand-blue mt-4">
                    {`By signing-in, you agree to ReelFlow's `}
                    <a
                        href="https://www.reelflow.com/legal/terms-of-service-starter"
                        target="_blank"
                        className="underline hover:text-brand-ocean">
                        terms
                    </a>
                    {` and `}
                    <a
                        href="https://reelflow.com/legal/privacy"
                        target="_blank"
                        className="underline hover:text-brand-ocean">
                        privacy agreements
                    </a>
                </div>
            </form>
        </GuestLayout>
    );
}
